import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'store/hooks';
import { getProfile } from 'store/auth/actions';
import { ActionType } from 'store/auth/model';
import { workosAuthService, TwoFactorChallengeFields } from 'services/workos-auth.service';

interface CustomerProfile {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar: string | null;
}

interface SendMagicAuthCodeResponse {
  success: boolean;
  message: string;
  userType: string;
}

interface SendMagicAuthCodeParams {
  email: string;
  companyId?: string;
}

interface VerifyMagicAuthCodeResponse extends TwoFactorChallengeFields {
  success: boolean;
  access_token?: string;
  refresh_token?: string;
  scan_token?: string;
  isNewUser?: boolean;
  /**
   * VRG-500 — did this email already have an account BEFORE this verification?
   * `isNewUser` answers a different question ("first time at this company") and
   * reports FALSE for a brand-new gift recipient, because the send provisions
   * both the `users` row and the `company_users` link before they ever click.
   */
  hasAccount?: boolean;
}

interface VerifyMagicAuthCodeParams {
  email: string;
  code: string;
  embeddKey?: string;
  companyId?: string;
  /**
   * Typed name from the self-serve create-account form (VERA-837), threaded
   * through to `CustomerWorkOSCallbackUseCase` for new-owner provisioning.
   * Optional — omitted by the general sign-in surface.
   */
  firstName?: string;
  lastName?: string;
  /**
   * VRG-357 — the handle chosen on the create-account form, threaded down the same
   * path as the name so a brand-new owner is provisioned WITH one.
   *
   * Why it is collected at signup rather than left to the profile page: the handle is
   * how another person addresses you in a transfer, and the lookup is now handle-only
   * (recipient search no longer matches on email — that was an enumeration oracle).
   * An account auto-assigned something like `johnd6ab` is effectively unaddressable:
   * nobody can guess it, and its owner has no reason to know it exists. Two accounts
   * both displaying "John Doe" and separated only by that string is precisely how a
   * gift went to the wrong person during VERA-304's verification.
   */
  username?: string;
  /**
   * VERA-837 — Terms & Conditions acceptance from the self-serve
   * create-account form's checkbox. Omitted by the general sign-in surface.
   */
  acceptedTerms?: boolean;
  termsVersion?: string;
}

interface LoginWithPasswordParams {
  email: string;
  password: string;
  companyId?: string;
  embeddKey?: string;
}

interface LoginWithPasswordResponse extends TwoFactorChallengeFields {
  success: boolean;
  // VRG-490 §2 — optional: absent on a challengeRequired/enrollmentRequired response.
  access_token?: string;
  refresh_token?: string;
  scan_token?: string;
  isNewUser?: boolean;
}

interface VerifyChallengeParams {
  ticket: string;
  code: string;
}

interface VerifyChallengeResponse {
  success: boolean;
  access_token?: string;
  refresh_token?: string;
  scan_token?: string;
  isNewUser?: boolean;
  usedRecoveryCode?: boolean;
  remainingRecoveryCodes?: number;
}

interface SetupPasswordResponse {
  success: boolean;
  message: string;
}

/**
 * Hook to send Magic Auth code (WorkOS OTP)
 */
export const useSendMagicAuthCode = () => {
  return useMutation<SendMagicAuthCodeResponse, Error, SendMagicAuthCodeParams>({
    mutationFn: ({ email, companyId }: SendMagicAuthCodeParams) =>
      workosAuthService.sendMagicAuthCode(email, companyId),
    // Auth actions must fail fast. The global QueryClient default retries
    // mutations once (retryDelay 2s), which would leave the button stuck on
    // "Verifying…"/"Sending…" for seconds on a 401 before the error shows.
    retry: false,
  });
};

/**
 * Hook to verify Magic Auth code and authenticate
 */
export const useVerifyMagicAuthCode = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<VerifyMagicAuthCodeResponse, Error, VerifyMagicAuthCodeParams>({
    mutationFn: ({ email, code, companyId, embeddKey, firstName, lastName, username, acceptedTerms, termsVersion }) =>
      workosAuthService.verifyMagicAuthCode({
        email,
        code,
        companyId,
        embeddKey,
        firstName,
        lastName,
        username,
        acceptedTerms,
        termsVersion,
      }),
    // Never retry: a wrong/expired code must surface the error immediately, not
    // re-submit the same code after the global 2s mutation-retry delay.
    retry: false,
    onSuccess: (data) => {
      // VRG-490 §2 — `success: true` alone no longer means "authenticated":
      // a `challengeRequired` response is ALSO `success: true` (the primary
      // factor checked out), but withholds the token pending the second
      // factor. Dispatching SET_AUTH here would tell `apps/app`'s
      // presence-based boot check the user is signed in before the
      // challenge is ever shown — exactly the failure mode the design doc's
      // §2 ticket-not-a-session-token decision exists to avoid. Only an
      // actual token in storage means authenticated.
      if (!data.access_token) return;
      // Invalidate and refetch customer profile
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      dispatch({ type: ActionType.SET_AUTH, payload: true });
      dispatch(getProfile());
    },
  });
};

/**
 * Hook to authenticate with email + password
 */
export const useLoginWithPassword = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<LoginWithPasswordResponse, Error, LoginWithPasswordParams>({
    mutationFn: ({ email, password, companyId, embeddKey }) =>
      workosAuthService.loginWithPassword({ email, password, companyId, embeddKey }),
    retry: false,
    onSuccess: (data) => {
      // VRG-490 §2 — see the identical guard in useVerifyMagicAuthCode above.
      if (!data.access_token) return;
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      dispatch({ type: ActionType.SET_AUTH, payload: true });
      dispatch(getProfile());
    },
  });
};

/**
 * VRG-490 §2 — completes a login paused by a `challengeRequired` ticket.
 * Same onSuccess contract as the two hooks above: only dispatch SET_AUTH
 * once a real token has landed in storage.
 */
export const useVerifyChallenge = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation<VerifyChallengeResponse, Error, VerifyChallengeParams>({
    mutationFn: ({ ticket, code }) => workosAuthService.verifyChallenge(ticket, code),
    retry: false,
    onSuccess: (data) => {
      if (!data.access_token) return;
      queryClient.invalidateQueries({ queryKey: ['customer-profile'] });
      dispatch({ type: ActionType.SET_AUTH, payload: true });
      dispatch(getProfile());
    },
  });
};

/**
 * Hook to set/update the authenticated user's password
 */
export const useSetupPassword = () => {
  return useMutation<SetupPasswordResponse, Error, string>({
    mutationFn: (password: string) => workosAuthService.setupPassword(password),
    retry: false,
  });
};

/**
 * Hook to get customer profile
 */
export const useCustomerProfile = (enabled = true) => {
  return useQuery<CustomerProfile | null, Error>({
    queryKey: ['customer-profile'],
    queryFn: () => workosAuthService.getProfile(),
    enabled: enabled && workosAuthService.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
};

/**
 * Hook to logout customer
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, void>({
    mutationFn: () => workosAuthService.logout(),
    onSuccess: () => {
      // Clear all queries
      queryClient.clear();
    },
  });
};

/**
 * Hook to check authentication status
 */
export const useIsAuthenticated = (): boolean => {
  return workosAuthService.isAuthenticated();
};
